var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from "react";
import { Box, Typography, Card, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { OTPInput } from "../../../components/inputs/OtpInput";
import { SButton } from "../../../components/buttons/SButton";
import { centered, colCenter } from "@ui/components/style/style";
import { useNavigate } from "react-router";
import { toast } from "@ui/utils/toaster";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { verifyOtp, resendOtp } from "@reducers/auth/thunk/auth";
import { clearAuthErrors } from "@reducers/auth/slice/auth";
import localStorageAccessor from "@utility/local-storage-accessor";
const OTPPage = () => {
    const { t } = useTranslation(["common", "validation"]);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useAppDispatch();
    const { loading, resendLoading, isAuthenticated } = useAppSelector((state) => state.auth);
    const [email, setEmail] = useState("");
    const [countdown, setCountdown] = useState(120);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    useEffect(() => {
        const storedEmail = localStorageAccessor.getOtpEmail();
        if (storedEmail) {
            setEmail(storedEmail);
        }
        else {
            navigate("/insight/login");
        }
        dispatch(clearAuthErrors());
    }, []);
    // Countdown timer effect
    useEffect(() => {
        if (countdown > 0) {
            const timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
        else {
            setIsResendDisabled(false);
        }
    }, [countdown]);
    const { control, handleSubmit, formState: { errors: formErrors }, setError, } = useForm({
        defaultValues: { otp: "" },
    });
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (data.otp.length !== 6) {
            setError("otp", { message: t("validation:otpSixDigit") });
            return;
        }
        try {
            yield dispatch(verifyOtp({ email, otpCode: data.otp }))
                .unwrap()
                .then(() => {
                toast.success(t("common:toasts.otpVerified"));
                navigate("/dashboard");
                localStorageAccessor.removeOtpEmail();
            })
                .catch((error) => {
                toast.error(error);
            });
        }
        catch (error) {
            if (error.errors) {
                error.errors.forEach((errorMsg) => {
                    toast.error(errorMsg);
                });
            }
            else {
                toast.error(error.message || t("common:toasts.invalidOtpCode"));
            }
        }
    });
    const handleResendCode = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield dispatch(resendOtp(email)).unwrap();
            toast.success(t("common:toasts.otpCodeResent"));
            setCountdown(120);
            setIsResendDisabled(true);
        }
        catch (error) {
            console.error("Error resending OTP:", error);
            if (error.errors) {
                error.errors.forEach((errorMsg) => {
                    toast.error(errorMsg);
                });
            }
            else {
                toast.error(error.message || t("common:toasts.failedToResendOtp"));
            }
        }
    });
    // Format countdown as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));
    return (React.createElement(Box, { sx: {
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            position: "relative",
            px: { xs: 1, md: 2 },
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(/assets/images/login-bg.jpg)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                zIndex: -1,
            },
            "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "#00000040", // Dark overlay
                zIndex: -1,
            },
        } },
        React.createElement(Card, { sx: Object.assign(Object.assign({ width: "100%", maxWidth: isSm ? 345 : 494, minHeight: isMobile ? 450 : 566, borderRadius: 4, px: isSm ? 5 : undefined, py: isSm ? 10 : undefined, backgroundColor: "#FFFFFFE5", backdropFilter: "blur(40px)" }, centered), { flexDirection: "column" }) },
            React.createElement(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 4,
                } },
                React.createElement("img", { src: "/assets/images/logo.svg", alt: "logo" })),
            React.createElement(Box, { maxWidth: 354 },
                React.createElement(Typography, { variant: "body2", color: "textSecondary", mb: 1.5, align: "center" }, t("common:codeSent", { email }))),
            React.createElement(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.2,
                    width: "100%",
                    maxWidth: 354,
                } },
                React.createElement(Controller, { name: "otp", control: control, rules: {
                        required: t("validation:required", { field: "OTP" }),
                    }, render: ({ field }) => {
                        var _a;
                        return (React.createElement(OTPInput, Object.assign({}, field, { length: 6, error: (_a = formErrors.otp) === null || _a === void 0 ? void 0 : _a.message })));
                    } }),
                React.createElement(SButton, { type: "submit", variant: "contained", size: "large", color: "inherit", fullWidth: true, loading: loading }, t("common:actions.verify"))),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { my: 2 }) },
                React.createElement(Typography, { variant: "body2", color: "textTertiary" }, t("common:dontHaveCode")),
                React.createElement(SButton, { variant: "text", color: "inherit", sx: { color: "primary.darker" }, onClick: handleResendCode, loading: resendLoading, disabled: isResendDisabled || resendLoading }, t("common:resendCode", {
                    counter: isResendDisabled ? `(${formatTime(countdown)})` : "",
                }))),
            React.createElement(SButton, { variant: "text", color: "inherit", sx: { color: "primary.darker" }, onClick: () => navigate("/insight/login") }, t("common:returnToSignin")))));
};
export default OTPPage;
